import { RetiredMarker } from '@/components/compliance/RetiredMarker';
import { ReviewDueChip } from '@/components/compliance/review/ReviewDueChip';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ExternalLink, ChevronRight, Check, Minus, AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { CONTROL_GUIDANCE_FIELDS, hasText } from '@/lib/control-guidance';
import { GuidanceBody, SectionHeading } from '@/components/compliance/ControlGuidance';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CTS_OPEN_STATES, CTS_PEOPLE_DETAIL, CTS_PEOPLE_HELP, CTS_PEOPLE_LABEL, CTS_REASSIGNABLE_STATES,
  ctsNextStates, ctsWorkflowLabel, CTS_NOT_YET_ASSESSED_LABEL, type CtsWorkflowState,
} from '@grc/shared';
import { ASSIGN_BTN, PeopleLaneCard, PeoplePicker } from '@/components/compliance/PeopleLanes';
import { AssessmentForm } from '@/components/compliance/assessment/AssessmentForm';
import { ApproverReview } from '@/components/compliance/assessment/ApproverReview';
import { StartAssessmentForm } from '@/components/compliance/assessment/StartAssessmentForm';
import { AssessmentHistory } from '@/components/compliance/assessment/AssessmentHistory';
import { reasonLabel } from '@/components/compliance/assessment/AssessmentContext';
import { START_ACTION, statusLabel, workflowErrorText } from '@/components/compliance/assessment/assessment-copy';
import { EvidenceLocationPanel } from '@/components/compliance/EvidenceLocationPanel';
import { CapturedEvidencePanel } from '@/components/compliance/CapturedEvidencePanel';
import { useControlEvidenceArtifacts } from '@/api/hooks/useEvidenceArtifacts';
import {
  changeLabelFor, EVIDENCE_GROUP, healthLabelFor, HEALTH_NEVER_CHECKED, INTEGRITY_SHORT_VERSION_ONLY,
  RECENCY_LABEL, AVAILABILITY_ROW_LABEL,
} from '@/components/compliance/evidence-copy';
import type { EvidenceArtifact } from '@/api/types/evidence-artifacts.types';
import { InfoDot } from '@/components/compliance/InfoDot';
import { CARD_SURFACE } from '@/components/compliance/card-surface';
import { useSetControlRole } from '@/api/hooks/useCtsWorkspace';
import { useAssignControl, useReassignTask, useTransitionTask } from '@/api/hooks/useCtsTasks';
import { useTeamMembers } from '@/api/hooks/useSettings';
import { useAuthStore } from '@/store/auth.store';
import { useControlHub } from '@/api/hooks/useCtsWorkspace';
import { ReviewSchedulePanel } from '@/components/compliance/review/ReviewSchedulePanel';
import { ControlAuditHistory } from '@/components/compliance/ControlAuditHistory';
import { formatCalendarDate } from '@/lib/calendar-date';
import type { ControlHub } from '@/api/types/cts-workspace.types';

/**
 * THE CONTROL DETAIL HUB — Tenant Convergence TC2, and the control-centric differentiator.
 *
 * ⚠ WHAT MAKES THIS A HUB RATHER THAN A PAGE WITH TABS. Until TC1 there was no key that
 * requirements, evidence, exceptions and tasks could share: assignments and evidence hung off
 * `pol_controls`, the catalogue spine was `mcc_`, and the two had zero control-level identifier
 * overlap. The page this supersedes said so in its own header and refused to draw the panels it
 * could not fill. `cts_control_state` is that key. This is the first surface on which one control
 * shows all of its work at once — the tenant moves
 * Control ↔ Requirement ↔ Evidence ↔ Monitoring ↔ Exceptions ↔ Tasks without re-establishing
 * context anywhere.
 *
 * ⚠ EVERY TAB CAN BE HONESTLY EMPTY. A control the tenant has never worked has no state row, so
 * every tab is empty and says so plainly. Nothing here renders a zero, a placeholder count, or a
 * greyed-out affordance for work that does not exist.
 */

/** One definition, shared with the lane cards so the two cannot drift. */
const PANEL = CARD_SURFACE;
const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';

const TABS = ['overview', 'requirements', 'evidence', 'monitoring', 'exceptions', 'tasks', 'audit'] as const;
type Tab = typeof TABS[number];
const TAB_LABEL: Record<Tab, string> = {
  overview: 'Overview',
  requirements: 'Requirements',
  evidence: 'Evidence',
  monitoring: 'Monitoring',
  exceptions: 'Exceptions',
  tasks: 'Tasks',
  audit: 'Audit history',
};

/**
 * The E3.2 mapping relationship, in MAPPING language.
 *
 * ⚠ Deliberately worded so it can never be read as a posture. "Partially addresses" is a statement
 * about a RELATIONSHIP between a control and one requirement; "Partially Implemented" is a
 * statement about the control alone. Same adverb, different subject, different layer.
 */
const RELATIONSHIP_LABEL: Record<string, string> = {
  satisfies: 'Satisfies',
  partial: 'Partially addresses',
  supports: 'Supports',
};

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-[16px] py-[18px] text-[13px] italic text-text-faint">{children}</p>;
}

/** Initials chip. Derived from the name we already show — no avatar storage exists to read. */
function Avatar({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
  return (
    <span aria-hidden="true"
      className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[5px] bg-bg-surface-2 text-[9.5px] font-semibold text-text-secondary">
      {initials}
    </span>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────────────────────

/**
 * A summary card. ⚠ Its headline is a WORD, not a score — "Ready", "Assessed", "Not Started".
 * Nothing here renders a percentage or a zero, because a control nobody has worked has not scored
 * 0; it has no measurement at all, and the two must not share a rendering.
 */
/**
 * ⚠ THERE IS NO `editable` PROP ANY MORE, AND ITS ABSENCE IS THE POINT. This card carried a pencil
 * behind an `editable` flag for surfaces whose write path was never built — owner, approver,
 * applicability, policy linking. A pencil that cannot edit reads as data merely waiting to be
 * entered, which is how a mislabelled chip elsewhere came to look like an owner had been set. Each
 * such card now STATES that the thing is not settable yet. Re-adding the flag would mean rebuilding
 * exactly the affordance that misled a retro-walk, so it is deleted rather than left unused.
 */
function Card({
  title, tone, headline, rows, onOpen, span, testId,
}: {
  title: string;
  tone: 'good' | 'idle' | 'warn';
  headline: string;
  /**
   * ⚠ A row with `heading: true` is a DIMENSION LABEL, not a datum. The Evidence card groups its
   *   rows by the question they answer (freshness / integrity / connection) because three
   *   visually-identical rows made a reader infer which signal each belonged to — and inferring is
   *   exactly what a compliance summary must not require.
   */
  rows: { label: string; value?: string | number; dot?: string; heading?: boolean }[];
  onOpen?: () => void;
  /** Extra grid classes — used to let one tile fill the space a 5-card row would leave empty. */
  span?: string;
  /** ⚠ So a test can assert over the WHOLE card rather than a text match that also hits the tab. */
  testId?: string;
}) {
  const mark = tone === 'good'
    ? <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-cert-valid-fg/15"><Check className="h-[13px] w-[13px] text-cert-valid-fg" aria-hidden="true" /></span>
    : tone === 'warn'
      ? <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-cert-warn-fg/15"><AlertTriangle className="h-[12px] w-[12px] text-cert-warn-fg" aria-hidden="true" /></span>
      : <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-text-faint/12"><Minus className="h-[13px] w-[13px] text-text-faint" aria-hidden="true" /></span>;

  return (
    <div className={cn(PANEL, 'flex h-full flex-col p-[15px]', span)} data-testid={testId}>
      <div className="mb-[12px] flex items-center gap-[8px]">
        <span className="text-[13px] font-semibold text-text-primary">{title}</span>
        {onOpen && (
          <button type="button" onClick={onOpen} aria-label={`Open ${title}`}
            className="ml-auto text-text-faint transition-colors hover:text-text-primary">
            <ChevronRight className="h-[14px] w-[14px]" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="mb-[12px] flex items-center gap-[10px]">
        {mark}
        <span className={cn('text-[16px] font-medium',
          tone === 'good' ? 'text-cert-valid-fg' : tone === 'warn' ? 'text-cert-warn-fg' : 'text-text-secondary')}>
          {headline}
        </span>
      </div>
      <dl className="space-y-[5px]">
        {rows.map((r, i) => (r.heading ? (
          <div key={`${r.label}-h`} className={cn('text-[10px] font-semibold uppercase tracking-[0.1em] text-text-faint',
            i > 0 && 'pt-[6px]')}>
            {r.label}
          </div>
        ) : (
          <div key={r.label} className="flex items-baseline gap-[8px] text-[11.5px]">
            {r.dot && <span className={cn('h-[7px] w-[7px] shrink-0 rounded-[2px]', r.dot)} />}
            <dt className="text-text-secondary">{r.label}</dt>
            {r.value !== undefined && <dd className="ml-auto tabular-nums text-text-primary">{r.value}</dd>}
          </div>
        )))}
      </dl>
    </div>
  );
}

function Overview({ hub, artifacts, onOpen }: {
  hub: ControlHub; artifacts: EvidenceArtifact[]; onOpen: (t: Tab) => void;
}) {
  const named = hub.requirements.filter((m) => m.framework_name !== null);
  const cited = hub.requirements.filter((m) => m.external_ref_id !== null);
  // ⚠ P1.4 — THREE DEFECTS FIXED HERE AT ONCE, all found at the preview gate.
  //   1. It counted `validity_state === 'expired'`, which the repoint made UNREACHABLE: the
  //      go-forward model has no validity window, so `stale` was permanently 0 and this card could
  //      never show a problem. A dead signal that renders as reassurance is worse than a wrong one.
  //   2. It said "Fresh" and "Stale" — the vocabulary of that same missing window.
  //   3. It read `hub.evidence`, a THIRD source for "does evidence exist" beside the location panel
  //      and the list. It now reads the same query they do, so the Overview cannot contradict the
  //      tab it summarises.
  // ⚠ ERI S2 (D-ERI-6/8) — "Captured" and every freshness / integrity count is about CURRENT
  //   evidence only, read from the api's projected availability (never re-derived here). An artifact
  //   whose newest check could not read it is still listed on the tab; here it is counted in its OWN
  //   row and drives Attention instead.
  const current = artifacts.filter((a) => a.availability === 'current');
  const removed = artifacts.filter((a) => a.availability === 'deleted_at_source').length;
  const unreadable = artifacts.filter((a) => a.availability === 'inaccessible').length;
  const reviewDue = current.filter((a) => a.review_recommended === true).length;
  const noAdvice = current.filter((a) => a.review_recommended === null).length;
  const within = current.filter((a) => a.review_recommended === false).length;
  const changed = current.filter(
    (a) => a.changed_since_capture || a.review_state === 'requires_re_review').length;
  // ⚠ HEALTH IS A TRUST INPUT, NOT A SEPARATE STATUS — so it reaches the SUMMARY, not only the tab.
  //   A green tick over "Captured" while the library is unreachable is exactly the reading the
  //   architecture forbids: evidence you can no longer verify must never read as evidence you can.
  //   ⓘ Found at the preview gate — the card ignored health entirely, so `access_lost` rendered green.
  const health = artifacts[0]?.health_state ?? null;
  // ⚠ §5.1e — the provider key for the container NOUN only. It is read from the same artifact the
  //   health state comes from, and it never reaches a conditional: it is passed to the shared
  //   derive functions, which answer for an unknown provider without guessing
  //   (D-PROVISION-REPOSITORY-UI-PROVIDER-AGNOSTIC-1).
  const repoKind = artifacts[0]?.repo_kind ?? null;
  const healthBroken = health !== null && health !== 'healthy';
  // ⚠ NEVER-CHECKED IS NEITHER. Making NULL an alarm would paint every control amber today (nothing
  //   checks health yet) and train people to ignore it; making it green would be the lie. It removes
  //   the green instead — stated, not shouted.
  const healthUnknown = health === null;
  // ⚠ The weaker integrity basis, counted so the summary can name it — MI asked whether the card
  //   distinguishes it, and now it does rather than leaving it to the detail alone.
  const versionOnly = current.filter((a) => !a.hash_algorithm).length;
  // ⚠ Captured evidence with NONE of it current is always attention, so the headline can never read
  //   "Captured" over a control whose evidence all failed its last check.
  const attention = reviewDue > 0 || changed > 0 || healthBroken || removed > 0 || unreadable > 0
    || (artifacts.length > 0 && current.length === 0);
  // ⚠ CAPR S2 — the DERIVED status (the latest approved decision), never the deprecated legacy axis.
  const currentStatus = hub.current_status;
  const approvals = hub.decisions.filter((d) => d.outcome_kind === 'approved').length;
  const openWork = hub.assignments.find((a) => CTS_OPEN_STATES.includes(a.workflow_status as CtsWorkflowState)) ?? null;

  const byFramework = new Map<string, number>();
  for (const m of named) byFramework.set(m.framework_name!, (byFramework.get(m.framework_name!) ?? 0) + 1);

  return (
    <div className="space-y-[12px] p-[15px]">
      {/*
        ⚠ WHAT THE CONTROL IS COMES FIRST. A reader arriving at this page needs to know what the
        control requires and which framework requirements it answers BEFORE they can judge posture
        or decide who should work it. Status tiles and the people lanes are operational questions,
        and they are asked further down — after the thing they are about has been described.
      */}
      {/* ⚠ THE SAME GRID AS THE TILES BELOW, so the column boundary is shared rather than
          approximated. A ratio like 2.2fr/1fr lands close to the tiles' thirds but never exactly —
          the gap arithmetic differs between a two-column and a three-column row — and "nearly
          aligned" reads worse than plainly different. Details takes two columns, mappings one, and
          the seam runs straight down the page. */}
      <div className="grid gap-[12px] md:grid-cols-2 xl:grid-cols-3">
        <div className={cn(PANEL, 'p-[15px] md:col-span-2')}>
          <span className="mb-[12px] block text-[13px] font-semibold text-text-primary">
            Control Details
          </span>

          {/* ⚠ SHORT METADATA STAYS COMPACT — three facts on one line rather than three rows that
              push the description below the fold. They are look-up values, not reading. */}
          <dl className="mb-[14px] flex flex-wrap gap-x-[28px] gap-y-[8px]">
            {([
              ['Control ID', hub.control.display_code],
              ['Domain · Family', `${hub.control.domain_name} · ${hub.control.family_name}`],
              ['Applicability',
                hub.applicability ? hub.applicability.replace(/_/g, ' ') : 'Not determined'],
            ] as const).map(([k, v]) => (
              <div key={k}>
                <dt className={cn(EYEBROW, 'block')}>{k}</dt>
                <dd className="mt-[3px] text-[12.5px] text-text-primary">{v}</dd>
              </div>
            ))}
          </dl>

          {/* ⚠ THE DESCRIPTION IS CONTENT, NOT A METADATA ROW. It is what a person reads to decide
              whether a control is being satisfied at all, and it was set in the same faint, narrow
              column as the ID next to it — so the one field that has to be read looked like the
              ones that only have to be found. Full width, body-size, primary text colour. */}
          <div className="border-t border-border-subtle pt-[12px]">
            {/* ⓘ The same section heading (icon + label) as the library's detail window, so the two read as one. */}
            <SectionHeading kind="description" label="Description" />
            {/* ⚠ NO WIDTH CAP. A 72ch cap is good typography on a full-width article and wrong
                here: this panel is narrower than that anyway on most screens, so the cap did
                nothing except wrap the description early while empty space sat beside it. It reads
                as body content and uses the width the row actually has. */}
            <p className="mt-[6px] text-[13px] leading-[1.65] text-text-primary">
              {hub.control.description ?? (
                <span className="italic text-text-faint">No description recorded for this control.</span>
              )}
            </p>
            {/* ⚠ THE AUTHORED GUIDANCE, directly below the description and read the same way — it is content a
                person reads to do the work. Each field renders only when authored: an unauthored one is ABSENT
                (no label, no placeholder), so the panel never shows an empty heading. Rendered by the same
                component as the library's detail window: lines the author wrote become bullets / evidence rows,
                a single line stays a paragraph, and the text is never split or rewritten. */}
            {CONTROL_GUIDANCE_FIELDS.filter((f) => hasText(hub.control[f.key])).map((f) => (
              <div key={f.key} className="mt-[16px]" data-testid={`control-guidance-${f.key}`}>
                <SectionHeading kind={f.key} label={f.label} />
                <div className="mt-[8px]">
                  <GuidanceBody kind={f.key} text={hub.control[f.key] ?? ''} dense />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={cn(PANEL, 'p-[15px] md:col-span-2 xl:col-span-1')}>
          <span className="mb-[10px] block text-[13px] font-semibold text-text-primary">Framework mappings</span>
          <div className="flex flex-wrap gap-[6px]">
            {[...byFramework.keys()].map((n) => (
              <span key={n} className="rounded-[4px] bg-bg-surface-2 px-[8px] py-[2px] font-mono text-[10.5px] text-text-secondary">{n}</span>
            ))}
            {cited.map((m) => (
              <span key={m.external_ref_id} title="External reference — no content held for that framework"
                className="rounded-[4px] border border-dashed border-cert-neutral-border px-[8px] py-[2px] font-mono text-[10.5px] text-text-faint">
                {m.external_ref_id}
              </span>
            ))}
          </div>
          {byFramework.size > 0 && (
            // ⚠ The do-once-satisfy-many sentence, said in words the tenant can actually read.
            <p className="mt-[10px] text-[11.5px] leading-[1.5] text-text-faint">
              Appears once across {byFramework.size} active {byFramework.size === 1 ? 'program' : 'programs'}
              {' '}— the work is shared, never duplicated per framework.
            </p>
          )}
        </div>
      </div>
      <div className="grid gap-[12px] md:grid-cols-2 xl:grid-cols-3">
        <Card
          title="Framework requirements"
          tone={hub.requirements.length > 0 ? 'good' : 'idle'}
          headline={hub.requirements.length > 0 ? `${hub.requirements.length} mapped` : 'Not mapped'}
          onOpen={() => onOpen('requirements')}
          rows={[
            ...[...byFramework.entries()].map(([name, n]) => ({ label: name, value: n, dot: 'bg-sky-400' })),
            // ⚠ Cited edges are COUNTED, never named — there is no framework row to name, and
            //    resolving one would need the mcc_ join this module has never made.
            ...(cited.length > 0
              ? [{ label: 'External references', value: cited.length, dot: 'bg-text-faint/50' }] : []),
          ]}
        />
        <Card
          testId="evidence-summary-card"
          title="Evidence"
          tone={artifacts.length === 0 ? 'idle' : attention ? 'warn' : healthUnknown ? 'idle' : 'good'}
          // ⚠ "Captured", not "Ready": this card knows evidence EXISTS and is within the catalogue's
          //    suggested cadence. It knows nothing about whether it is SUFFICIENT — that is the
          //    link's review state, which P2 owns — and "Ready" would quietly claim it.
          headline={artifacts.length === 0 ? 'None captured' : attention ? 'Attention' : 'Captured'}
          onOpen={() => onOpen('evidence')}
          // ⚠ GROUPED BY DIMENSION. Each row now sits under the question it answers, so a reader
          //    never has to work out whether "Review recommended" is about age, change or access.
          //    ⓘ The grouping is PRESENTATION — the same three signals, the same semantics, in the
          //      same places they were computed. What changed is that the card says which is which.
          rows={[
            { label: EVIDENCE_GROUP.freshness, heading: true },
            { label: RECENCY_LABEL.within, value: within, dot: 'bg-cert-valid-fg' },
            { label: RECENCY_LABEL.due, value: reviewDue, dot: 'bg-cert-warn-fg' },
            // ⚠ Its own line, never folded into either count above: an artifact whose cadence is not
            //    a period is neither within a recommendation nor past one.
            ...(noAdvice > 0
              ? [{ label: RECENCY_LABEL.none, value: noAdvice, dot: 'bg-text-faint/50' }] : []),

            // ⚠ INTEGRITY appears only when there is something to say about it. An empty heading
            //    would imply a check that did not happen.
            ...(changed > 0 || versionOnly > 0 || removed > 0 || unreadable > 0
              ? [{ label: EVIDENCE_GROUP.integrity, heading: true }] : []),
            // ⚠ ERI S2 — ONLY WHEN NON-ZERO (the existing conditional-row pattern), and each on its own
            //    line: "Removed" and "Unreadable" ask the tenant for different actions.
            ...(removed > 0
              ? [{ label: AVAILABILITY_ROW_LABEL.deleted_at_source, value: removed, dot: 'bg-cert-warn-fg' }] : []),
            ...(unreadable > 0
              ? [{ label: AVAILABILITY_ROW_LABEL.inaccessible, value: unreadable, dot: 'bg-cert-warn-fg' }] : []),
            ...(changed > 0
              ? [{ label: changeLabelFor('content_changed', repoKind), value: changed, dot: 'bg-cert-warn-fg' }] : []),
            // ⚠ The WEAKER BASIS, surfaced on the summary too: a file with no hash is change-detected
            //    only by version, and that is a different promise from the checksum case.
            ...(versionOnly > 0
              ? [{ label: INTEGRITY_SHORT_VERSION_ONLY, value: versionOnly, dot: 'bg-text-faint/50' }] : []),

            // ⚠ CONNECTION IS ALWAYS NAMED, including when it is healthy. Stating it only when broken
            //    made "no row" mean two different things — verified, or simply not looked at.
            { label: EVIDENCE_GROUP.connection, heading: true },
            { label: healthUnknown ? HEALTH_NEVER_CHECKED : healthLabelFor(health!, repoKind),
              dot: health === 'healthy' ? 'bg-cert-valid-fg'
                : healthUnknown ? 'bg-text-faint/50' : 'bg-cert-warn-fg' },
          ]}
        />
        {/*
          ⚠ MONITORING SPANS BOTH ROWS. Five tiles in a three-column grid leave a hole under the
          third, and an empty cell reads as something that failed to load rather than as nothing to
          show. Monitoring takes the column because it is the tile whose content grows — assessment
          history accumulates, where Policy and Approval are single facts.
        */}
        <Card
          span="xl:row-span-2"
          title="Monitoring"
          tone={currentStatus ? 'good' : 'idle'}
          headline={statusLabel(currentStatus?.implementation_status ?? null)}
          onOpen={() => onOpen('monitoring')}
          rows={[
            { label: 'Approved assessments', value: approvals, dot: 'bg-text-faint/50' },
            ...(currentStatus
              ? [{ label: `Approved ${fmt(currentStatus.approved_at)}`, dot: 'bg-cert-warn-fg' }]
              : []),
          ]}
        />
        {/*
          ⚠ PLACEHOLDER, AND IT SAYS SO RATHER THAN SHOWING A GREEN TICK. There is no link between
          a ccl_ catalogue control and a tenant policy document — no column, no table, no join. A
          card reading "Linked" would assert a capability that does not exist, which is exactly the
          mistake the superseded page refused to make with its Evidence card.
        */}
        <Card
          title="Policy document" tone="idle" headline="Not linked"
          rows={[{ label: 'Linking a policy is not available in this release', dot: 'bg-text-faint/40' }]}
        />
        <Card
          title="Approval"
          // ⚠ CAPR S2 — THE HEADLINE SAYS WHERE APPROVAL ACTUALLY IS. It read "In progress" whenever ANY
          //   assignment existed, so a decided control showed a green card saying "In progress". Open
          //   work leads (it is what happens next); otherwise the latest approval; otherwise nothing yet.
          tone={!openWork && currentStatus ? 'good' : 'idle'}
          headline={openWork?.workflow_status === 'submitted_for_review' ? 'Awaiting decision'
            : openWork ? 'Assessment in progress'
            : currentStatus ? 'Approved' : CTS_NOT_YET_ASSESSED_LABEL}
          onOpen={() => onOpen('tasks')}
          rows={[
            { label: currentStatus ? `Last approved ${fmt(currentStatus.approved_at)}${currentStatus.approver_name ? ` by ${currentStatus.approver_name}` : ''}`
                : 'No approved assessment', dot: 'bg-text-faint/50' },
            ...(hub.control.approver_name
              ? [{ label: `Approver: ${hub.control.approver_name}`, dot: 'bg-sky-400' }]
              : [{ label: 'No approver named', dot: 'bg-text-faint/40' }]),
          ]}
        />
      </div>

      {/*
        ⚠ THE PEOPLE AREA — three lanes, one grammar, three separate questions.
        Assignee answers "who is working it now, through review" (a PROCESS, `cts_assignments`).
        Responsible answers "who performs this control" and Approver "who approves it" (PROPERTIES,
        `cts_control_state`). They are grouped because a reader wants all three people-facts in one
        glance — and kept in SEPARATE labelled cards because grouping is not merging. No action
        here sets two lanes at once.
      */}
      <div>
        {/* ⚠ Named for what it governs, not for what it contains. "People" described the nouns
            on the cards; this describes the three decisions the section is actually for. */}
        <span className={cn(EYEBROW, 'mb-[8px] block')}>Assignments &amp; Accountability</span>
        <div className="grid gap-[12px] lg:grid-cols-3">
          <WorkflowPanel hub={hub} />
          <RaciLane hub={hub} lane="responsible" />
          <RaciLane hub={hub} lane="accountable" />
        </div>
      </div>


    </div>
  );
}

// ── Requirements ──────────────────────────────────────────────────────────────────────────────

function Requirements({ hub }: { hub: ControlHub }) {
  if (hub.requirements.length === 0) {
    return <Empty>This control is not mapped to any requirement in your workspace.</Empty>;
  }
  return (
    <ul>
      {hub.requirements.map((m, i) => (
        <li key={`${m.requirement_reference ?? m.external_ref_id}-${i}`}
            className="border-b border-border-subtle px-[16px] py-[11px] last:border-b-0">
          {m.framework_name !== null ? (
            <>
              <span className="text-[11px] text-text-secondary">{m.framework_name}</span>
              <span className="mt-[2px] flex items-baseline gap-[8px]">
                <span className="font-mono text-[12px] text-text-secondary">{m.requirement_display_code}</span>
                <span className="text-[13px] text-text-primary">{m.requirement_title}</span>
              </span>
            </>
          ) : (
            /*
              ⚠ A CITED EDGE, AND ITS MISSING NAME IS THE PROPERTY — NOT A GAP.
              An external citation records that a platform author linked this control to a
              requirement in a framework this platform holds no content for. There is no row to
              resolve, so there is no name to show and nothing to open. O4 verified exactly this on
              the single live cited edge in production. A future change that "helpfully" resolved
              the name would be a regression that looks like an improvement.
            */
            <>
              <span className="text-[11px] italic text-text-faint">External reference — no page to open</span>
              <span className="mt-[2px] flex items-baseline gap-[8px]">
                <ExternalLink className="h-[12px] w-[12px] text-text-faint" aria-hidden="true" />
                <span className="font-mono text-[12px] text-text-secondary">{m.external_ref_id}</span>
              </span>
            </>
          )}
          {/*
            ⚠ TWO PARTIALS, TWO LAYERS — and they must not blur.
            `relationship_type` says how fully THIS CONTROL answers THAT REQUIREMENT (the E3.2
            mapping vocabulary). Control posture says how completely the control itself is
            implemented. A fully Implemented control can still only PARTIALLY ADDRESS a demanding
            requirement, and the reverse is equally possible — so the mapping is worded in mapping
            language ("Partially addresses"), never "Partially Implemented", and is rendered as a
            quiet outlined chip rather than in the posture palette that carries the other meaning.
          */}
          <span className="mt-[5px] inline-flex items-center rounded-[4px] border border-cert-neutral-border px-[7px] py-[1px] text-[10px] text-text-secondary">
            {RELATIONSHIP_LABEL[m.relationship_type] ?? m.relationship_type}
          </span>
          {m.rationale && <p className="mt-[4px] text-[12px] text-text-secondary">{m.rationale}</p>}
        </li>
      ))}
    </ul>
  );
}

// ── Evidence ──────────────────────────────────────────────────────────────────────────────────
//
// ⚠ P1.4 REMOVED THE LOCAL LIST. It rendered the superseded model's vocabulary — "Expired",
//   "Valid until", "No expiry recorded" — over a column the go-forward model does not have, so it
//   could only ever have said something false or something empty. The tab now renders
//   CapturedEvidencePanel, which reads the P1.4 endpoint and speaks in recency ADVICE instead.


// ── Monitoring ────────────────────────────────────────────────────────────────────────────────

function Monitoring({ hub }: { hub: ControlHub }) {
  // ⚠ CAPR S2 (FR-N5) — THE CONTROL'S ASSESSMENT HISTORY IS THE DECISION LEDGER: approved decisions,
  //   their same-cycle returns nested, and returns no approval followed behind "Show returned".
  return (
    <>
      <AssessmentHistory hub={hub} />
      {/*
        ⚠ NAMED, NOT PAPERED OVER. This tab is the control's posture over time — it is NOT a
        continuous-assurance feed. `fab_assertions` keys its coverage by mcc_ public ids and there
        is no mcc_↔ccl_ control bridge, so connecting one here would mean inventing a join this
        module has never made. Saying so is the same discipline the superseded page applied when it
        refused to draw an empty Evidence card.
      */}
      <p className="border-t border-border-subtle px-[16px] py-[9px] text-[11px] text-text-faint">
        Assessment history only. Automated assurance signals are not connected to this control.
      </p>
    </>
  );
}

// ── Exceptions and Tasks ──────────────────────────────────────────────────────────────────────

function Exceptions({ hub }: { hub: ControlHub }) {
  if (hub.exceptions.length === 0) return <Empty>No exceptions have been raised for this control.</Empty>;
  return (
    <ul>
      {hub.exceptions.map((x) => (
        <li key={x.id} className="border-b border-border-subtle px-[16px] py-[11px] last:border-b-0">
          <div className="flex items-baseline gap-[10px]">
            <span className="min-w-0 flex-1 truncate text-[13px] text-text-primary">{x.title}</span>
            <span className="text-[11px] capitalize text-text-secondary">{x.status}</span>
          </div>
          <p className="mt-[3px] text-[12px] text-text-secondary">{x.justification}</p>
          {/* Expiry is derived at read exactly as evidence freshness is, and for the same reason. */}
          {x.expiry_state === 'lapsed' && (
            <p className="mt-[3px] text-[11px] font-medium text-cert-expired-fg">
              Expired {fmt(x.expires_at)}
            </p>
          )}
          {x.expiry_state === 'active' && (
            <p className="mt-[3px] text-[11px] text-text-secondary">Expires {fmt(x.expires_at)}</p>
          )}
        </li>
      ))}
    </ul>
  );
}


// ── The Workflow region ───────────────────────────────────────────────────────────────────────
//
// ⚠ THIS IS NOT THE OWNER CARD, AND THE WHOLE POINT IS THAT NOBODY CONFUSES THEM. Owner answers
// "who is accountable for this control, standing" — a property, RACI, and NOT SETTABLE anywhere
// in the product yet (its card says so rather than showing a pencil that cannot edit). Workflow
// answers "who is working it right now, and where has that work got to" — a process, the eight-state
// machine, one open assignment at a time. They are different questions with different vocabularies,
// so this region never says "Owner" and the Owner card never says "Assignee".
//
// ⚠ EVERY AFFORDANCE COMES FROM `ctsNextStates()`. The transition map is not re-encoded here: a
// button exists because the machine says the move is legal from this state for this actor, and for
// no other reason. A surface with its own copy of the rules is a surface that will eventually offer
// a move the endpoint refuses — the failure this sprint exists to end.
const WORKFLOW_REVIEWER_ROLES = ['risk_manager', 'super_admin'];

function WorkflowPanel({ hub }: { hub: ControlHub }) {
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const isReviewer = useAuthStore((s) => s.roles.some((r) => WORKFLOW_REVIEWER_ROLES.includes(r)));
  const { data: team } = useTeamMembers();
  // ⓘ The same query the Evidence tab and the overview read — React Query dedupes it to one request.
  const { data: evidenceRead } = useControlEvidenceArtifacts(hub.control.control_reference);
  const artifacts = evidenceRead?.artifacts ?? [];
  const assign = useAssignControl();
  const transition = useTransitionTask();
  const reassign = useReassignTask();

  const [mode, setMode] = useState<null | 'start' | 'reassign' | 'assess'>(null);
  const [pick, setPick] = useState('');
  const [pendingTo, setPendingTo] = useState<CtsWorkflowState | null>(null);
  // ⚠ CAPR S2 — THE SERVER'S REFUSAL, IN ITS OWN WORDS, told apart by code (workflowErrorText). The
  //   generic "not saved" line is only for a failure the server did not explain.
  const [error, setError] = useState<string | null>(null);

  // ⚠ `unassigned` IS THE ABSENCE OF AN OPEN ROW, never a stored value — so it is derived here the
  //   same way the database defines it, rather than read off a column that would have to lie.
  const open = hub.assignments.find(
    (a) => CTS_OPEN_STATES.includes(a.workflow_status as CtsWorkflowState)) ?? null;

  // ⚠ DECIDED WORK STILL HAS TO BE VISIBLE. `approved` and `completed` are not open, so a card
  //   driven only by `open` goes blank the moment work finishes — losing "decided by" at exactly
  //   the point it carries the most weight. `shown` is what the region DISPLAYS; `open` is what it
  //   lets you ACT on. Conflating the two is what hid the chain, and a test caught it.
  //   ⓘ assignments arrive newest-first (ORDER BY created_at DESC).
  const shown = open ?? hub.assignments[0] ?? null;

  // ⚠ CAPR S2 — WHO MAY DECIDE, BY THE SERVER'S RULE (D-CAPR-APPROVER-EXCLUSIVITY): the control's
  //   named Approver ALONE when one is named — with or without a reviewer role — otherwise the
  //   reviewer floor. A role-only gate offered Approve to people the server refuses, and hid it from
  //   the one person it accepts.
  const canDecide = hub.control.accountable_user_id
    ? hub.control.accountable_user_id === viewerId
    : isReviewer;

  // ⚠ The same actor rule the endpoint enforces: the decisions follow `canDecide`; assignee moves
  //   need the assignee — and a reviewer may also make them. A button is offered only when the
  //   server would accept it.
  const moves = open
    ? ctsNextStates(open.workflow_status as CtsWorkflowState).filter((tr) => (
      tr.by === 'reviewer' ? canDecide : (isReviewer || open.assignee_user_id === viewerId)))
    : [];
  // ⚠ The submit and the two decisions are not one-click moves: submitting opens the assessment
  //   screen, and deciding happens in the approver view.
  const plainMoves = moves.filter((tr) => tr.to !== 'submitted_for_review' && tr.to !== 'approved' && tr.to !== 'returned');
  // ⚠ CAPR S2 (SCR) — only the ASSIGNEE authors the proposal, by the server's rule; a reviewer who is
  //   not the assignee reassigns the work to themselves to assess it.
  const canSubmit = moves.some((tr) => tr.to === 'submitted_for_review') && open?.assignee_user_id === viewerId;
  const awaitingDecision = open?.workflow_status === 'submitted_for_review';

  const canReassign = !!open && isReviewer
    && CTS_REASSIGNABLE_STATES.includes(open.workflow_status as CtsWorkflowState);

  const busy = assign.isPending || transition.isPending || reassign.isPending;

  const reset = () => { setMode(null); setPick(''); setPendingTo(null); };
  const begin = (m: typeof mode) => { setError(null); setMode(m); };
  const onError = (err: unknown) => setError(workflowErrorText(err));

  const submitReassign = () => {
    if (!pick || !open) return;
    reassign.mutate({ id: open.id, assigneeUserId: pick }, { onSuccess: reset, onError });
  };

  const submitMove = () => {
    if (!open || !pendingTo) return;
    transition.mutate({ id: open.id, to: pendingTo }, { onSuccess: reset, onError });
  };

  return (
    <div className={cn(PANEL, 'flex h-full flex-col p-[15px]')} data-testid="people-lane-assignee">
      <div className="flex items-start gap-[8px]">
        {/* ⚠ "Assignee", from the same label map as its two siblings. The card was titled
            "Workflow" — the name of the AXIS rather than of the person it names, which made one of
            three otherwise-identical cards read as a different kind of thing. */}
        <span className="text-[13px] font-semibold text-text-primary">{CTS_PEOPLE_LABEL.assignee}</span>
        <InfoDot label={CTS_PEOPLE_LABEL.assignee} detail={CTS_PEOPLE_DETAIL.assignee} />
      </div>
      {/* The disambiguator is part of the design, not decoration: two people-shaped cards sit in
          this grid and a reader must be able to tell them apart without opening either. */}
      {/*
        ⚠ The disambiguator names the OTHER lane in order to separate from it — the one legitimate
        use of a neighbouring lane's word inside this region. Re-pointed when the rendered
        vocabulary changed Owner → Responsible; the separation it protects is unchanged.
      */}
      <p className="mb-[12px] mt-[3px] text-[11px] leading-[1.45] text-text-faint">
        {CTS_PEOPLE_HELP.assignee} — distinct from the {CTS_PEOPLE_LABEL.responsible} for this control.
      </p>

      <div className="mb-[10px] flex items-center gap-[10px]">
        <span className={cn('rounded-full px-[9px] py-[2px] text-[11.5px]',
          open ? 'bg-sky-500/12 text-sky-300' : 'bg-text-faint/12 text-text-secondary')}>
          {open ? ctsWorkflowLabel(open.workflow_status) : 'Not assigned'}
        </span>
        <span className="text-[13px] text-text-primary">
          {open?.assignee_name ?? <span className="italic text-text-faint">Nobody assigned</span>}
        </span>
      </div>

      <dl className="mb-[12px] space-y-[4px] text-[11.5px] text-text-secondary">
        {!open && shown && (
          <div>
            <dt className="inline">Most recent work </dt>
            <dd className="inline">{ctsWorkflowLabel(shown.workflow_status)} — {shown.assignee_name ?? 'unassigned'}</dd>
          </div>
        )}
        {shown?.due_at && <div><dt className="inline">Due </dt><dd className="inline">{fmt(shown.due_at)}</dd></div>}
        {shown?.submitted_at && <div><dt className="inline">Submitted </dt><dd className="inline">{fmt(shown.submitted_at)}</dd></div>}
        {/* ⚠ The actor chain is SHOWN, and that is all it is. This machine does not stop a reviewer
            approving work they submitted, so the card reports who acted and claims nothing more. */}
        {shown?.assigned_by_name && <div><dt className="inline">Assigned by </dt><dd className="inline">{shown.assigned_by_name}</dd></div>}
        {shown?.decided_by_name && <div><dt className="inline">Decided by </dt><dd className="inline">{shown.decided_by_name}</dd></div>}
        {open && reasonLabel(open.reason_started) && (
          <div><dt className="inline">Started for </dt><dd className="inline">{reasonLabel(open.reason_started)}</dd></div>
        )}
        {open?.initiation_note && <div><dt className="inline">Note </dt><dd className="inline">{open.initiation_note}</dd></div>}
      </dl>

      {shown?.returned_reason && !awaitingDecision && (
        <p className="mb-[12px] text-[12px] text-cert-warn-fg">Returned — {shown.returned_reason}</p>
      )}

      {/* ── actions ── */}
      {mode === 'start' ? (
        <StartAssessmentForm hub={hub} team={team} busy={busy} error={error} onCancel={reset}
          onStart={(v) => assign.mutate({ controlReference: hub.control.control_reference, ...v },
            { onSuccess: reset, onError })} />
      ) : mode === 'assess' && open ? (
        <AssessmentForm hub={hub} assignment={open} artifacts={artifacts} busy={busy} error={error} onCancel={reset}
          onSubmit={(proposal) => transition.mutate({ id: open.id, to: 'submitted_for_review', proposal },
            { onSuccess: reset, onError })} />
      ) : mode === 'reassign' ? (
        <div className="flex flex-col gap-[8px]">
          <label className="text-[11.5px] text-text-secondary" htmlFor="wf-assignee">Reassign to</label>
          {/* ⚠ The SHARED picker — one eligibility rule rendered one way, detail and bulk alike. */}
          <PeoplePicker id="wf-assignee" lane="assignee" team={team} value={pick} onChange={setPick} />
          <div className="flex gap-[8px]">
            <button type="button" onClick={submitReassign} disabled={busy || !pick}
              className="rounded-[7px] bg-accent-cyan/15 px-[11px] py-[6px] text-[12px] text-accent-cyan disabled:opacity-50">
              Confirm
            </button>
            <button type="button" onClick={reset} disabled={busy}
              className="rounded-[7px] px-[11px] py-[6px] text-[12px] text-text-secondary">Cancel</button>
          </div>
        </div>
      ) : awaitingDecision && open ? (
        <ApproverReview hub={hub} assignment={open} artifacts={artifacts} canDecide={canDecide} busy={busy}
          error={error}
          onApprove={() => { setError(null); transition.mutate({ id: open.id, to: 'approved' }, { onSuccess: reset, onError }); }}
          onReturn={(reason) => { setError(null); transition.mutate({ id: open.id, to: 'returned', reason }, { onSuccess: reset, onError }); }} />
      ) : pendingTo ? (
        <div className="flex gap-[8px]">
          <button type="button" onClick={submitMove} disabled={busy}
            className="rounded-[7px] bg-accent-cyan/15 px-[11px] py-[6px] text-[12px] text-accent-cyan disabled:opacity-50">
            Confirm — {ctsWorkflowLabel(pendingTo)}
          </button>
          <button type="button" onClick={reset} disabled={busy}
            className="rounded-[7px] px-[11px] py-[6px] text-[12px] text-text-secondary">Cancel</button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-[8px]">
          {!open && (
            <button type="button" onClick={() => begin('start')} disabled={busy} className={ASSIGN_BTN}>
              {START_ACTION}
            </button>
          )}
          {plainMoves.map((tr) => (
            <button
              key={tr.to} type="button" onClick={() => { setError(null); setPendingTo(tr.to); }} disabled={busy}
              className="rounded-[7px] border border-border-subtle bg-bg-surface px-[11px] py-[6px] text-[12px] text-text-primary disabled:opacity-50">
              {ctsWorkflowLabel(tr.to)}
            </button>
          ))}
          {canSubmit && (
            <button type="button" onClick={() => begin('assess')} disabled={busy}
              className="rounded-[7px] bg-accent-cyan/15 px-[11px] py-[6px] text-[12px] text-accent-cyan disabled:opacity-50">
              {open?.proposed_implementation_status ? 'Continue assessment' : 'Assess this control'}
            </button>
          )}
          {canReassign && (
            <button type="button" onClick={() => begin('reassign')} disabled={busy}
              className="rounded-[7px] border border-border-subtle bg-bg-surface px-[11px] py-[6px] text-[12px] text-text-secondary disabled:opacity-50">
              Reassign
            </button>
          )}
          {/* ⓘ An honest empty: when the machine offers this viewer nothing, the card says so
              rather than showing greyed buttons that imply a permission they do not hold. */}
          {open && plainMoves.length === 0 && !canSubmit && !canReassign && (
            <span className="text-[11.5px] italic text-text-faint">
              No action available to you on this work.
            </span>
          )}
        </div>
      )}
      {error && mode === null && !awaitingDecision && (
        <p className="mt-[9px] text-[11.5px] text-cert-warn-fg" role="alert">{error}</p>
      )}
    </div>
  );
}

/**
 * One RACI lane, wired. ⚠ Both lanes render ALWAYS — an unset Approver is a state, not an absence
 * of the feature, and rendering it only when set is what made it invisible before this sprint.
 */
function RaciLane({ hub, lane }: { hub: ControlHub; lane: 'responsible' | 'accountable' }) {
  const setRole = useSetControlRole();
  // Hoisted: a hook belongs at the top of the render, not inside a JSX prop.
  const { data: team } = useTeamMembers();
  const currentUserId = lane === 'responsible'
    ? hub.control.responsible_user_id : hub.control.accountable_user_id;
  const currentName = lane === 'responsible'
    ? hub.control.owner_name : hub.control.approver_name;
  // ⚠ COMPARED BY ID, NOT BY NAME. Two colleagues can share a display name, and a note claiming
  //    "the same person" on the strength of matching text would be wrong exactly when it mattered.
  const sameAsOther = Boolean(
    hub.control.responsible_user_id
    && hub.control.responsible_user_id === hub.control.accountable_user_id);

  return (
    <PeopleLaneCard
      lane={lane}
      currentName={currentName}
      currentUserId={currentUserId}
      sameAsOther={sameAsOther}
      team={team}
      busy={setRole.isPending}
      onSet={(userId) => setRole.mutate({
        controlReference: hub.control.control_reference, role: lane, userId,
      })}
      onClear={() => setRole.mutate({
        controlReference: hub.control.control_reference, role: lane, userId: null,
      })}
    />
  );
}

function Tasks({ hub }: { hub: ControlHub }) {
  // ⚠ THE ACTION LIVES HERE TOO, NOT JUST ON OVERVIEW. This tab previously said "Nobody is
  //   assigned" and offered nothing, while the Tasks workspace told people to assign from the
  //   detail page — a loop with no way out. The same panel renders in both places, so whichever
  //   route a person takes, the action is where they were sent.
  return (
    <div>
      <div className="border-b border-border-subtle p-[16px]">
        <WorkflowPanel hub={hub} />
      </div>
      {hub.assignments.length === 0
        ? <Empty>No work has been assigned on this control yet.</Empty>
        : <TaskHistory hub={hub} />}
    </div>
  );
}

/** Every assignment this control has carried, newest first — including decided ones. */
function TaskHistory({ hub }: { hub: ControlHub }) {
  return (
    <ul>
      {hub.assignments.map((g) => (
        <li key={g.id} className="border-b border-border-subtle px-[16px] py-[11px] last:border-b-0">
          <div className="flex items-baseline gap-[10px]">
            <span className="text-[13px] text-text-primary">{g.assignee_name ?? 'Unassigned'}</span>
            {/*
              ⚠ Read through the SHARED state vocabulary (D-TC-WORKFLOW-STATES), not prettified
              from the raw column. The database still holds V1039's six shipped values while the
              ruled lifecycle has eight; TC4 re-bakes the CHECK and migrates. Rendering the raw
              string here would put a second, silently-diverging vocabulary in the UI — and the
              first thing to break would be `sent_back`, which the ruled set calls "Returned".
            */}
            <span className="text-[11px] text-text-secondary">
              {ctsWorkflowLabel(g.workflow_status)}
            </span>
            <span className="ml-auto text-[11px] text-text-secondary">
              {g.due_at ? `Due ${fmt(g.due_at)}` : 'No due date'}
            </span>
          </div>
          {g.returned_reason && (
            // ⚠ The reason is ASSIGNEE-VISIBLE — it is the point of Returned. A state that sends work
            //    back without saying why makes the assignee guess at the reviewer's objection.
            <p className="mt-[3px] text-[12px] text-cert-warn-fg">Returned — {g.returned_reason}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

// ── The hub ───────────────────────────────────────────────────────────────────────────────────

export function ControlHubPage() {
  const { reference } = useParams<{ reference: string }>();
  const { data: hub, isLoading, isError } = useControlHub(reference);
  // ⚠ ONE QUERY FOR "WHAT EVIDENCE EXISTS", read here and by the two panels below. React Query
  //   dedupes by key, so this is one request and one cache entry — three consumers that cannot
  //   disagree, rather than three that must be remembered.
  const { data: evidenceRead } = useControlEvidenceArtifacts(reference ?? '');
  const artifacts = evidenceRead?.artifacts ?? [];
  // ⓘ CAPR S2 — `?tab=` opens a tab directly: the Tasks page sends "Assess" here, to the assessment
  //   screen on the Tasks tab. An unrecognised value falls back to the overview.
  const [params] = useSearchParams();
  const requested = params.get('tab');
  const [tab, setTab] = useState<Tab>((TABS as readonly string[]).includes(requested ?? '') ? (requested as Tab) : 'overview');
  // P1.2 — provisioning an evidence location is a workspace-manager action, like the reviewer lanes.
  const canProvision = useAuthStore((s) => s.roles.some((r) => WORKFLOW_REVIEWER_ROLES.includes(r)));
  // ⚠ CAPR S3 — who may change a control's review cadence: the roles the server accepts (affordance only).
  const canSetReview = useAuthStore((s) => s.roles.some((r) => WORKFLOW_REVIEWER_ROLES.includes(r)));

  if (isLoading) {
    return (
      <div className="space-y-[12px] p-[24px]">
        <Skeleton className="h-[24px] w-[260px]" />
        <Skeleton className="h-[220px] w-full" />
      </div>
    );
  }
  if (isError || !hub) {
    return (
      <div className="space-y-[10px] p-[24px]">
        <Link to="/compliance/workspace/controls" className="inline-flex items-center gap-[6px] text-[12px] text-text-secondary hover:text-text-primary">
          <ArrowLeft className="h-[13px] w-[13px]" aria-hidden="true" /> Controls
        </Link>
        {/*
          ⚠ ONE MESSAGE FOR "GONE" AND "NOT YOURS TO SEE". The api answers 404 for both, because a
          403 would confirm the reference names real catalogue content — which is the fact the
          content gate exists to withhold. The copy must not undo that by distinguishing them.
        */}
        <p className="text-[13px] text-text-secondary">
          This control is not available in your workspace.
        </p>
      </div>
    );
  }

  // ⚠ COUNTS ON TABS ARE REAL OR ABSENT. A tab with nothing in it shows no number rather than a
  //   zero: "0" reads as a measured result, and "not started" is not a measurement.
  const counts: Record<Tab, number> = {
    overview: 0,
    audit: 0,
    requirements: hub.requirements.length,
    // ⚠ The tab badge counts the SAME artifacts the tab renders (hub.evidence is the api's legacy
    //   projection and is no longer read by this page).
    evidence: artifacts.length,
    // ⚠ CAPR S2 — the approved decisions the history tab lists, not the deprecated legacy rows.
    monitoring: hub.decisions.filter((d) => d.outcome_kind === 'approved').length,
    exceptions: hub.exceptions.length,
    tasks: hub.assignments.length,
  };

  const current = hub.current_status;

  return (
    <div className="space-y-[16px] p-[24px]">
      <Link to="/compliance/workspace/controls" className="inline-flex items-center gap-[6px] text-[12px] text-text-secondary transition-colors hover:text-text-primary">
        <ArrowLeft className="h-[13px] w-[13px]" aria-hidden="true" /> Controls
      </Link>

      <header className="space-y-[6px]">
        <p className="text-[11px] text-text-secondary">
          {hub.control.domain_name} · {hub.control.family_name}
        </p>
        <div className="flex flex-wrap items-baseline gap-[10px]">
          <span className="font-mono text-[13px] text-text-secondary">{hub.control.display_code}</span>
          {/* ⚠ The hub is where someone decides whether to collect MORE evidence for this
              control — so it is the one place the fact most has to be present. */}
          <RetiredMarker retiredAt={hub.control.retired_at} size="md" />
          <h1 className="text-[19px] font-semibold text-text-primary">{hub.control.title}</h1>
          {/* ⚠ CAPR S5 — the same server label the list's chip shows, from the same resolver. */}
          <ReviewDueChip label={hub.review.review_label} />
        </div>
        {/* ⚠ 70ch wrapped this onto two lines with half the row empty. The cap is raised rather
            than removed: unbounded body text on an ultrawide monitor is genuinely hard to read, so
            it still wraps — just when the line is actually long, not at an arbitrary column. */}
        {hub.control.description && (
          <p className="max-w-[120ch] text-[13px] leading-[1.55] text-text-secondary">
            {hub.control.description}
          </p>
        )}
      </header>

      {/*
        ⚠ THE POSTURE STRIP READS "NOT STARTED" WHEN THERE IS NO STATE ROW — not 0%, not Unknown.
        `has_state` is a distinct fact from every assessment outcome, and TC1 removed the schema's
        ability to blur them (no default result, no implicit row). The UI must not put it back.
      */}
      <div className={cn(PANEL, 'flex flex-wrap items-center gap-x-[28px] gap-y-[10px] px-[16px] py-[12px]')}
           data-testid="posture-strip">
        <div>
          <span className={cn(EYEBROW, 'block')}>Posture</span>
          <span className="mt-[3px] block text-[13px] text-text-primary">
            {/* ⚠ CAPR S2 — the DERIVED status. No approved decision is "Not Yet Assessed" (D-CAPR-STATUS-SET
                retired "Not Started"), distinct from every assessed value, never 0%. */}
            {current ? statusLabel(current.implementation_status)
              : <span className="italic text-text-faint">{CTS_NOT_YET_ASSESSED_LABEL}</span>}
          </span>
        </div>
        <div>
          <span className={cn(EYEBROW, 'block')}>Applicability</span>
          <span className="mt-[3px] block text-[13px] capitalize text-text-primary">
            {hub.applicability
              ? hub.applicability.replace(/_/g, ' ')
              : <span className="italic text-text-faint">Not determined</span>}
          </span>
        </div>
        <div>
          <span className={cn(EYEBROW, 'block')}>Last assessed</span>
          <span className="mt-[3px] block text-[13px] text-text-primary">
            {/* ⚠ CAPR S3 — the tenant-local approval DATE the server resolved, the same day the next
                review is counted from (a browser-local rendering of the instant could be a day apart). */}
            {formatCalendarDate(hub.review.last_approved_date) ?? <span className="italic text-text-faint">Never</span>}
          </span>
        </div>
        {/*
          ⚠ OWNER AND APPROVER ARE TWO PEOPLE-FACTS, NAMED SEPARATELY. TC1 shipped
          `responsible_user_id` and `accountable_user_id` as distinct columns; collapsing them into
          one "owner" here would erase a distinction the schema deliberately holds — the person who
          does the work is not the person who accepts it, and the approval flow depends on that.
        */}
        <div>
          <span className={cn(EYEBROW, 'block')}>{CTS_PEOPLE_LABEL.responsible}</span>
          <span className="mt-[3px] flex items-center gap-[7px] text-[13px] text-text-primary">
            {hub.control.owner_name ? (
              <>
                <Avatar name={hub.control.owner_name} />
                {hub.control.owner_name}
              </>
            ) : (
              // Same honesty as the Owner card below: not-yet-settable, not merely empty.
              <span className="italic text-text-faint" title="Setting an owner is not available in this release">
                Not set
              </span>
            )}
          </span>
        </div>
        <div>
          <span className={cn(EYEBROW, 'block')}>{CTS_PEOPLE_LABEL.accountable}</span>
          <span className="mt-[3px] flex items-center gap-[7px] text-[13px] text-text-primary">
            {hub.control.approver_name ? (
              <>
                <Avatar name={hub.control.approver_name} />
                {hub.control.approver_name}
              </>
            ) : (
              // ⚠ The SAME empty as Responsible beside it. "Not named" against "Not set" reads as
              //    two different kinds of nothing, and a reader has to work out whether one of them
              //    means something is wrong. Both lanes are unset the same way.
              <span className="italic text-text-faint" title="Nobody is Approver on this control yet">
                Not set
              </span>
            )}
          </span>
        </div>
      </div>

      {/* ⚠ CAPR S3 — the review schedule, resolved for every control (a never-worked one included). */}
      <ReviewSchedulePanel hub={hub} canChange={canSetReview} />

      <div className={PANEL}>
        <div role="tablist" aria-label="Control detail" className="flex flex-wrap gap-[2px] border-b border-border-subtle px-[8px] pt-[8px]">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              id={`tab-${t}`}
              aria-selected={tab === t}
              aria-controls={`panel-${t}`}
              onClick={() => setTab(t)}
              className={cn(
                'rounded-t-[6px] px-[12px] py-[7px] text-[12px] transition-colors',
                tab === t
                  ? 'bg-bg-surface-2 font-medium text-text-primary'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {TAB_LABEL[t]}
              {counts[t] > 0 && <span className="ml-[6px] text-[11px] text-text-faint">{counts[t]}</span>}
            </button>
          ))}
        </div>
        <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
          {tab === 'overview' && <Overview hub={hub} artifacts={artifacts} onOpen={setTab} />}
          {tab === 'requirements' && <Requirements hub={hub} />}
          {tab === 'evidence' && (
            <>
              {/* ⚠ WHERE the evidence lives sits ABOVE the evidence itself, as its own fact. */}
              <EvidenceLocationPanel reference={hub.control.control_reference} canProvision={canProvision} />
              {/* ⚠ P1.4: the GO-FORWARD render. It replaces a list that read the superseded table and
                  spoke of expiry the model never had — the folder above, the evidence itself here. */}
              <CapturedEvidencePanel reference={hub.control.control_reference} />
            </>
          )}
          {tab === 'monitoring' && <Monitoring hub={hub} />}
          {tab === 'exceptions' && <Exceptions hub={hub} />}
          {tab === 'tasks' && <Tasks hub={hub} />}
          {/* ⚠ CAPR — the control's audit history, read from audit_logs (risk_manager / super_admin). */}
          {tab === 'audit' && <ControlAuditHistory reference={reference ?? ''} canView={canSetReview} />}
        </div>
      </div>
    </div>
  );
}
